import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { formatINR } from "@/lib/roles";

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const timerRef = useRef();
  const nav = useNavigate();

  useEffect(() => {
    if (!q.trim()) { setResult(null); return; }
    setLoading(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/search?q=${encodeURIComponent(q)}`);
        setResult(data);
      } catch { setResult(null); }
      setLoading(false);
    }, 300);
    return () => clearTimeout(timerRef.current);
  }, [q]);

  const goto = (path) => { setOpen(false); setQ(""); nav(path); };

  return (
    <div className="relative w-full max-w-md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search properties, receipts, users…"
          data-testid="global-search-input"
          className="w-full h-9 pl-9 pr-9 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2942]/40"
        />
        {q && (
          <button onClick={() => { setQ(""); setResult(null); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {open && q && (
        <div className="absolute z-40 mt-2 w-full max-h-96 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl">
          {loading && <div className="p-4 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Searching…</div>}
          {!loading && result && (
            <div className="p-2 text-sm">
              {result.properties?.length > 0 && (
                <div className="mb-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 px-2 py-1">Properties</div>
                  {result.properties.map((p) => (
                    <button key={p.id} onClick={() => goto(`/properties/${p.id}`)} className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800">
                      <div className="font-medium">{p.property_id} · {p.owner_name}</div>
                      <div className="text-xs text-slate-500 truncate">{p.address}</div>
                    </button>
                  ))}
                </div>
              )}
              {result.consumers?.length > 0 && (
                <div className="mb-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 px-2 py-1">Water Consumers</div>
                  {result.consumers.map((c) => (
                    <button key={c.id} onClick={() => goto(`/water/${c.id}`)} className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800">
                      <div className="font-medium">{c.consumer_id} · {c.consumer_name}</div>
                    </button>
                  ))}
                </div>
              )}
              {result.payments?.length > 0 && (
                <div className="mb-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 px-2 py-1">Receipts</div>
                  {result.payments.map((p) => (
                    <button key={p.id} onClick={() => goto(`/receipts/${p.receipt_number || p.payment_id}`)} className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800">
                      <div className="font-medium">{p.receipt_number || p.payment_id} · {formatINR(p.amount)}</div>
                    </button>
                  ))}
                </div>
              )}
              {result.users?.length > 0 && (
                <div className="mb-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 px-2 py-1">Users</div>
                  {result.users.map((u) => (
                    <div key={u.id} className="px-2 py-1.5">
                      <div className="font-medium">{u.name}</div>
                      <div className="text-xs text-slate-500">{u.email} · {u.role}</div>
                    </div>
                  ))}
                </div>
              )}
              {!result.properties?.length && !result.consumers?.length && !result.payments?.length && !result.users?.length && (
                <div className="p-4 text-sm text-slate-500">No results found.</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
